import type {
  GridInitialState,
  GridSortModel,
} from '@packages/fenrir/components/datagrid';

import type { AssetType } from '../../../assets/asset-type';
import { ASSETS_DATA_GRID_STATE_KEY } from '../asset-table.constants';

const defaultSort: GridSortModel = [{ field: 'lastSeenTime', sort: 'desc' }];

/**
 * Get the initial sort model for the assets table based on the asset type and Fenrir V2 feature flag.
 * If Fenrir V2 feature is not enabled, it returns the default sort model.
 * If Fenrir V2 feature is enabled, it retrieves the sort model from session storage.
 * If the sort model is not found or invalid, it returns the default sort model.
 * @param assetType - The type of asset (e.g., 'host', 'container', etc.).
 * @param isFenrirV2FeatureEnabled - A boolean indicating whether the Fenrir V2 feature is enabled.
 * @returns The initial sort model for the assets table.
 * @remarks
 * - The default sort model is set to sort by 'lastSeenTime' in descending order.
 * - The function handles errors gracefully by returning the default sort model if any issues occur while retrieving the sort model from session storage.
 */
export const getInitialSortModel = ({
  assetType,
  isFenrirV2FeatureEnabled,
}: {
  assetType: AssetType;
  isFenrirV2FeatureEnabled: boolean;
}): GridSortModel => {
  // If Fenrir V2 feature is not enabled, return the default sort model
  // no need to check session storage
  if (!isFenrirV2FeatureEnabled) {
    return defaultSort;
  }
  try {
    const stateFromSessionStorage = sessionStorage.getItem(
      ASSETS_DATA_GRID_STATE_KEY,
    );

    if (!stateFromSessionStorage) {
      return defaultSort;
    }

    const gridState = JSON.parse(stateFromSessionStorage) as Record<
      AssetType,
      GridInitialState
    >;

    if (!gridState || typeof gridState !== 'object') {
      return defaultSort;
    }

    const sortModel = gridState[assetType]?.sorting?.sortModel;

    if (!sortModel || !Array.isArray(sortModel)) {
      return defaultSort;
    }

    return sortModel;
  } catch {
    return defaultSort;
  }
};
